import { SET_HIGHLIGHT_DETECTION_STATE, SET_HIGHLIGHT_DETECTION_JOB, SET_HIGHLIGHT_DETECTION_ACTIVE } from '../../constants/mutations';
import { STATE } from '@videotools/js/one/components/highlights/enums';
import ApiClient from '@videotools/js/api';
import { getPeriods } from '@js/logic/game';
import bus, { EVENT } from '@videotools/js/events';

const getInitialState = () => ({
	state:              STATE.IDLE,
	job:                null,
	jobMonitorInterval: null,
	isHighlightsMode:   false,
	isActive:           false,
});

const isRunningState = (status) => {
	return status === 'created' || status === 'running';
};

const isFailedState = (status) => {
	return status === 'cancelled' || status === 'failed';
};

export default {
	namespaced: true,
	state:      getInitialState,
	mutations:  {
		[SET_HIGHLIGHT_DETECTION_STATE]: (state, value) => {
			state.state = value;
		},
		[SET_HIGHLIGHT_DETECTION_JOB]: (state, value) => {
			state.job = value;
		},
		[SET_HIGHLIGHT_DETECTION_ACTIVE]: (state, value) => {
			state.isActive = value;
		},
	},
	actions: {
		startDetection: async({ commit, rootGetters, state, dispatch }, options = {}) => {
			const event = rootGetters['event/event'];
			const selectedGroup = rootGetters['tagGroups/selectedTagGroup'];
			const settings = {
				profile:    options.profile,
				enhancedAi: options.enhancedAi,
				createTags: options.createTags,
			};

			let periods = null;
			if (options.gamePhasesOnly) {
				const tags = rootGetters['event/tags'];
				periods = getPeriods({ event, tags })
					.filter(period => period.type === 'action')
					.map(period => {
						return {
							start: period.start,
							end:   period.end,
						};
					});

				if (periods.length === 0) {
					bus.publish(EVENT.ERROR, $I18n.trans('highlights.audio_detection.tagging.no_phases_found'));
					return;
				}

				settings.periods = periods;
			}

			try {
				commit(SET_HIGHLIGHT_DETECTION_STATE, STATE.CREATED);
				commit(SET_HIGHLIGHT_DETECTION_ACTIVE, true);
				const response = await ApiClient.startHighlightDetection(event.id, selectedGroup.id, settings);

				if (isRunningState(response.status)) {
					commit(SET_HIGHLIGHT_DETECTION_JOB, response);
					await dispatch('monitorJob', response.jobId);
				} else {
					if (state.jobMonitorInterval) {
						commit(SET_HIGHLIGHT_DETECTION_STATE, STATE.IDLE);
						clearInterval(state.jobMonitorInterval);
					}
				}
			} catch (e) {
				bus.publish(EVENT.ERROR, $I18n.trans('highlights.audio_detection.tagging.init_failed'));
				commit(SET_HIGHLIGHT_DETECTION_STATE, STATE.IDLE);
				console.error('Error starting detection', e);
			};
		},
		monitorJob: async({ commit, state }, jobId) => {
			state.jobMonitorInterval = setInterval(async() => {
				try {
					const monitorResponse = await ApiClient.getHighlightDetectionStatus(jobId);

					if (monitorResponse && monitorResponse.status) {
						commit(SET_HIGHLIGHT_DETECTION_STATE, monitorResponse.status);
						commit(SET_HIGHLIGHT_DETECTION_JOB, monitorResponse);
					}

					if (!monitorResponse || !isRunningState(monitorResponse.status)) {
						clearInterval(state.jobMonitorInterval);
					}
				} catch (e) {
					console.error('Error fetching detection job', e);
					clearInterval(state.jobMonitorInterval);
				}
			}, 5000);
		},
		cancelJob: async({ commit, state }) => {
			if (state.job?.jobId) {
				await ApiClient.cancelHighlightDetectionJob(state.job.jobId);
			}

			commit(SET_HIGHLIGHT_DETECTION_STATE, STATE.IDLE);
			commit(SET_HIGHLIGHT_DETECTION_JOB, null);
		},
		close: async({ commit }) => {
			commit(SET_HIGHLIGHT_DETECTION_STATE, STATE.IDLE);
			commit(SET_HIGHLIGHT_DETECTION_JOB, null);
			commit(SET_HIGHLIGHT_DETECTION_ACTIVE, false);
		},
		loadJobByEvent: async({ commit, rootGetters, dispatch }) => {
			const event = rootGetters['event/event'];
			const response = await ApiClient.getHighlightDetectionStatusByEvent(event.id);

			if (response && response.jobId && !isFailedState(response.status)) {
				commit(SET_HIGHLIGHT_DETECTION_STATE, response.status);
				commit(SET_HIGHLIGHT_DETECTION_JOB, response);

				await dispatch('monitorJob', response.jobId);
			}
		},
	},
	getters: {
		isHighlightsMode:    (state) => state.isHighlightsMode, // always false, no possibility to activate with new UI
		state:               (state) => state.state,
		job:                 (state) => state.job,
		isRunning:           (state) => state.state === STATE.RUNNING,
		isCreated:           (state) => state.state === STATE.CREATED,
		isCompleted:         (state) => state.state === STATE.COMPLETED,
		isIdle:              (state) => state.state === STATE.IDLE || state.state === STATE.CANCELLED,
		progressPct:         (state) => state.job?.progressPct || 0,
		isHighlightsEnabled: () => window.userContextName === 'asport',
		isActive:            (state) => state.isActive,
	},
};
